// Letting another app act as you: the consent step of Supabase Auth's OAuth 2.1
// server. Today the only client is the SiminoSocial app inside Canva, which uses
// the token to send designs to the media library.
//
// Supabase sends the person here with `?authorization_id=…` after checking the
// client and redirect URI. This page only shows who is asking and passes the
// decision back; the token it leads to is an ordinary session for this user, so
// every permission and RLS rule still applies to whatever the app does with it.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { logError } from "../lib/logger";
import { Loading, Spinner } from "../components/Spinner";

type Details = { authorizationId: string; clientName: string; email: string };

export function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id");
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"allow" | "deny" | null>(null);

  useEffect(() => {
    if (!authorizationId) {
      setError("This link is missing its connection request. Start again from the other app.");
      return;
    }
    supabase.auth.oauth.getAuthorizationDetails(authorizationId)
      .then(({ data, error: e }) => {
        if (e || !data) throw e ?? new Error("No connection request found");
        // Already allowed before: go straight back to the app that asked.
        if ("redirect_url" in data) {
          window.location.assign(data.redirect_url);
          return;
        }
        setDetails({
          authorizationId: data.authorization_id,
          clientName: data.client.name,
          email: data.user.email,
        });
      })
      .catch((e) => {
        setError("This connection request has expired or is not valid. Start again from the other app.");
        void logError("auth", e, { step: "oauth_consent_details" });
      });
  }, [authorizationId]);

  async function decide(choice: "allow" | "deny") {
    if (!details || busy) return;
    setBusy(choice);
    setError(null);
    const { data, error: e } = choice === "allow"
      ? await supabase.auth.oauth.approveAuthorization(details.authorizationId, { skipBrowserRedirect: true })
      : await supabase.auth.oauth.denyAuthorization(details.authorizationId, { skipBrowserRedirect: true });
    if (e || !data) {
      setBusy(null);
      setError("That did not go through. Try again, or start again from the other app.");
      void logError("auth", e ?? new Error("no redirect"), { step: `oauth_consent_${choice}` });
      return;
    }
    window.location.assign(data.redirect_url);
  }

  return (
    <div className="card">
      <h1>Connect an app</h1>
      {!details && !error && <Loading label="Checking the request…" />}
      {details && (
        <>
          <p>
            <strong>{details.clientName}</strong> wants to use your SiminoSocial account
            ({details.email}).
          </p>
          <p className="muted">
            It can see your workspace names and add files to the media library in workspaces
            where you can upload media. It cannot post or change anything else. You can
            disconnect it from the app at any time.
          </p>
          {error && <p className="err" role="alert">{error}</p>}
          <div className="row">
            <button
              className={`primary${busy === "allow" ? " is-busy" : ""}`}
              disabled={busy !== null}
              onClick={() => decide("allow")}
            >
              {busy === "allow" ? <><Spinner /> Connecting…</> : `Allow ${details.clientName}`}
            </button>
            <button
              className={busy === "deny" ? "is-busy" : undefined}
              disabled={busy !== null}
              onClick={() => decide("deny")}
            >
              {busy === "deny" ? <><Spinner /> Denying…</> : "Deny access"}
            </button>
          </div>
        </>
      )}
      {error && !details && (
        <>
          <p className="err" role="alert">{error}</p>
          <p className="muted"><Link to="/start">Back to SiminoSocial</Link></p>
        </>
      )}
    </div>
  );
}
